import { useParams } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import GiftPlayerCard from "@/components/gift/GiftPlayerCard";
import GiftShareBooster from "@/components/gift/GiftShareBooster";
import GiftMessageCard from "@/components/gift/GiftMessageCard";
import GiftParticles from "@/components/gift/GiftParticles";
import NotFound from "./NotFound";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { getTheme, getCoverGradient, adjustColor, getAccentForStyle, toGenitive } from "@/lib/gift-utils";

const GiftBoxSVG = ({ isDark, accentColor, isOpening }: { isDark: boolean; accentColor: string; isOpening?: boolean }) => (
  <svg width="160" height="160" viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <radialGradient id="boxGlow" cx="50%" cy="85%" r="55%">
        <stop offset="0%" stopColor={isDark ? "#D4AF37" : accentColor} stopOpacity={isDark ? "0.35" : "0.2"} />
        <stop offset="100%" stopColor={isDark ? "#D4AF37" : accentColor} stopOpacity="0" />
      </radialGradient>
      <linearGradient id="ribbonGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={accentColor} />
        <stop offset="100%" stopColor={adjustColor(accentColor, -40)} />
      </linearGradient>
      <linearGradient id="boxBody" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={isDark ? "#1A1A2A" : "#F0EFF5"} />
        <stop offset="100%" stopColor={isDark ? "#121220" : "#E5E4EC"} />
      </linearGradient>
      <linearGradient id="boxLid" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={isDark ? "#1E1E30" : "#F8F7FC"} />
        <stop offset="100%" stopColor={isDark ? "#161626" : "#EEECF5"} />
      </linearGradient>
    </defs>
    {/* Shadow ellipse */}
    <ellipse cx="80" cy="145" rx="55" ry="10" fill="url(#boxGlow)" />
    {/* Box body */}
    <rect x="20" y="70" width="120" height="68" rx="8"
      fill="url(#boxBody)"
      stroke={isDark ? "rgba(212,175,55,0.18)" : "rgba(108,99,255,0.12)"}
      strokeWidth="1.2" />
    {/* Box vertical ribbon */}
    <rect x="71" y="70" width="18" height="68" rx="2"
      fill={isDark ? "rgba(212,175,55,0.1)" : "rgba(108,99,255,0.06)"} />
    {/* Lid */}
    <g style={{ 
      transformOrigin: "80px 56px",
      animation: isOpening ? "gift-lid-open 500ms ease-out forwards" : undefined,
    }}>
      <rect x="14" y="50" width="132" height="24" rx="7"
        fill="url(#boxLid)"
        stroke={isDark ? "rgba(212,175,55,0.18)" : "rgba(108,99,255,0.12)"}
        strokeWidth="1.2" />
      {/* Lid horizontal ribbon */}
      <rect x="71" y="50" width="18" height="24" rx="2"
        fill={isDark ? "rgba(212,175,55,0.1)" : "rgba(108,99,255,0.06)"} />
    </g>
    {/* Ribbon bow left */}
    <path d="M80 50 C80 30 58 18 46 30 C34 42 58 50 80 50"
      stroke="url(#ribbonGrad)" strokeWidth="3.5" fill="none" strokeLinecap="round"
      style={{
        transformOrigin: "80px 50px",
        animation: isOpening ? "gift-bow-left 500ms ease-out forwards" : undefined,
      }}
    />
    {/* Ribbon bow right */}
    <path d="M80 50 C80 30 102 18 114 30 C126 42 102 50 80 50"
      stroke="url(#ribbonGrad)" strokeWidth="3.5" fill="none" strokeLinecap="round"
      style={{
        transformOrigin: "80px 50px",
        animation: isOpening ? "gift-bow-right 500ms ease-out forwards" : undefined,
      }}
    />
    {/* Center knot */}
    <circle cx="80" cy="50" r="5" fill={accentColor} />
  </svg>
);

const GiftPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [isOpened, setIsOpened] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [showParticles, setShowParticles] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [playCount, setPlayCount] = useState(0);
  const [completionPercent, setCompletionPercent] = useState(0);
  const [showShimmer, setShowShimmer] = useState(true);
  // Audio proxy availability: 'checking' | 'ok' | 'blocked'
  const [proxyStatus, setProxyStatus] = useState<"checking" | "ok" | "blocked">("checking");

  const { data: gift, isLoading, error } = useQuery({
    queryKey: ["gift-page", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gift_pages")
        .select("*")
        .eq("slug", slug!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const incrementViews = useMutation({
    mutationFn: async (id: string) => {
      await supabase.rpc("increment_gift_views" as any, { gift_id: id } as any).throwOnError();
    },
  });

  const incrementPlays = useMutation({
    mutationFn: async (id: string) => {
      await supabase.rpc("increment_gift_plays" as any, { gift_id: id } as any).throwOnError();
    },
  });

  useEffect(() => {
    if (gift?.id) {
      incrementViews.mutate(gift.id);
      const giftStyle = ((gift as any).gift_style as string) || 'light';
      trackEvent('gift_page_opened', { slug: gift.slug, hero_name: gift.hero_name, gift_id: gift.id, style: giftStyle });
      trackEvent('gift_preopen_viewed', { gift_id: gift.id, style: giftStyle, has_giver_name: !!(gift as any).sender_name, has_custom_message: !!(gift as any).custom_message });
      trackEvent('gift_preopen_style', { gift_id: gift.id, style: giftStyle });

      // Dynamic OG meta tags for social sharing
      const ogTags: Record<string, string> = {
        "og:title": `🎁 Эксклюзивный музыкальный подарок для ${gift.hero_name}`,
        "og:description": "Персональная песня от HitChef. Нажми и послушай 🎵",
        "og:type": "website",
        "og:url": `https://hitchef.ru/g/${gift.slug}`,
        "og:image": gift.cover_url || "https://hitchef.ru/og-image.png",
      };
      Object.entries(ogTags).forEach(([property, content]) => {
        let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
        if (!el) {
          el = document.createElement("meta");
          el.setAttribute("property", property);
          document.head.appendChild(el);
        }
        el.setAttribute("content", content);
      });
      document.title = `🎁 Музыкальный подарок для ${gift.hero_name} | HitChef`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gift?.id]);

  // Shimmer timeout
  useEffect(() => {
    const t = setTimeout(() => setShowShimmer(false), 2500);
    return () => clearTimeout(t);
  }, []);

  // Health-check audio proxy via hitchef.ru. If unreachable (blocked / DNS / 5xx),
  // we fall back to direct Supabase URL and show a warning banner.
  useEffect(() => {
    if (!gift?.slug) return;
    let cancelled = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);

    const proxyUrl = `https://hitchef.ru/functions/v1/audio-proxy?slug=${encodeURIComponent(gift.slug)}`;
    fetch(proxyUrl, { method: "HEAD", signal: controller.signal, cache: "no-store" })
      .then((res) => {
        if (cancelled) return;
        const ct = res.headers.get("content-type") || "";
        if (res.ok && ct.startsWith("audio/")) {
          setProxyStatus("ok");
        } else {
          setProxyStatus("blocked");
          trackEvent("gift_proxy_unreachable", {
            slug: gift.slug,
            status: res.status,
            content_type: ct,
          });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setProxyStatus("blocked");
        trackEvent("gift_proxy_unreachable", {
          slug: gift.slug,
          reason: err?.name === "AbortError" ? "timeout" : "network",
        });
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timeout);
    };
  }, [gift?.slug]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0D0D14" }}>
        <div className="w-10 h-10 border-2 border-[#6C63FF]/30 border-t-[#6C63FF] rounded-full animate-spin" />
      </div>
    );
  }

  if (!gift || error) return <NotFound />;

  const style = ((gift as any).gift_style as string) || "light";
  const isDark = style === "black";
  const theme = getTheme(style);
  const accentColor = getAccentForStyle(style, gift.accent_color || "#6C63FF");
  const coverSeed = `${gift.hero_name}-${gift.track_title}`;
  const coverGradient = getCoverGradient(coverSeed, isDark ? "black" : "light");
  const animationEnabled = gift.is_animation_enabled !== false;
  const customMessage = (gift as any).custom_message as string | null;
  const senderName = (gift as any).sender_name as string | null;
  const pageUrl = `https://hitchef.ru/g/${gift.slug}`;

  const handleOpen = () => {
    trackEvent('gift_open_clicked', { slug: gift.slug, hero_name: gift.hero_name, gift_id: gift.id, style: style, has_giver_name: !!senderName, has_custom_message: !!customMessage });
    setIsOpening(true);
    setShowParticles(true);
    setTimeout(() => {
      setIsOpened(true);
      trackEvent('gift_open_animation_completed', { slug: gift.slug, gift_id: gift.id, style: style });
    }, 900);
  };

  const showContent = !animationEnabled || isOpened;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-8 relative overflow-hidden"
      style={{ background: theme.bg }}
    >
      {/* Spotlight — top center */}
      <div
        className="fixed top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] pointer-events-none z-0"
        style={{
          background: isDark
            ? `radial-gradient(ellipse at center top, rgba(212,175,55,0.06), transparent 70%)`
            : `radial-gradient(ellipse at center top, rgba(108,99,255,0.06), transparent 70%)`,
        }}
      />

      {/* Breathing glow — center */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background: showContent
            ? `radial-gradient(circle at 50% 40%, ${theme.glowColor}, transparent 65%)`
            : `radial-gradient(circle at 50% 55%, ${theme.spotlightColor}, transparent 60%)`,
          animation: "gift-breathe 8s ease-in-out infinite",
        }}
      />

      {/* Ambient particles on opened content */}
      {showContent && (
        <GiftParticles accentColor={accentColor} isDark={isDark} mode="ambient" />
      )}

      <AnimatePresence mode="wait">
        {!showContent ? (
          /* ==================== PRE-OPEN SCREEN ==================== */
          <motion.div
            key="preopen"
            className="flex flex-col items-center text-center relative px-6 w-full max-w-[380px]"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.05, filter: "brightness(1.8)" }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            {/* Opening flash overlay */}
            {isOpening && (
              <motion.div
                className="fixed inset-0 z-50 pointer-events-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.7, 0] }}
                transition={{ duration: 0.6, times: [0, 0.3, 1] }}
                style={{
                  background: isDark
                    ? "radial-gradient(circle at 50% 45%, rgba(212,175,55,0.5), transparent 60%)"
                    : "radial-gradient(circle at 50% 45%, rgba(108,99,255,0.4), transparent 60%)",
                }}
              />
            )}

            {showParticles && <GiftParticles accentColor={accentColor} isDark={isDark} mode="burst" />}

            {/* Gift box with float + glow */}
            <motion.div
              className="relative z-10 mb-6"
              animate={isOpening 
                ? { scale: [1, 1.05, 1.1], y: [0, -5, -15], opacity: [1, 1, 0] } 
                : { scale: 1 }}
              transition={isOpening 
                ? { duration: 0.8, ease: "easeOut" } 
                : { duration: 0.3 }}
            >
              {/* Glow under box */}
              <div
                className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-[140px] h-[40px] pointer-events-none"
                style={{
                  background: isDark
                    ? "radial-gradient(ellipse, rgba(212,175,55,0.2), transparent 70%)"
                    : "radial-gradient(ellipse, rgba(108,99,255,0.12), transparent 70%)",
                  animation: "gift-glow-breathe 4s ease-in-out infinite",
                }}
              />
              <div style={{ animation: isOpening ? undefined : "gift-box-float 6s ease-in-out infinite" }}>
                <GiftBoxSVG isDark={isDark} accentColor={accentColor} isOpening={isOpening} />
              </div>
            </motion.div>

            {/* Recipient name — large personalized */}
            <motion.h1
              className="relative z-10 text-[26px] sm:text-[30px] font-bold tracking-[0.3px] mb-2"
              style={{ color: theme.textPrimary }}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              {gift.hero_name},
            </motion.h1>

            {/* Main text */}
            <motion.p
              className="relative z-10 text-[16px] leading-relaxed max-w-[280px] mb-1"
              style={{ color: theme.textSecondary }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.5 }}
            >
              для тебя подготовили особенный музыкальный подарок ✨
            </motion.p>

            {/* Subtitle */}
            <motion.p
              className="relative z-10 text-[13px] mb-2"
              style={{ color: theme.textTertiary }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              Песня, созданная специально для тебя
            </motion.p>

            {/* Giver name */}
            {senderName && (
              <motion.p
                className="relative z-10 text-[14px] font-medium mb-2"
                style={{ color: isDark ? accentColor : theme.textSecondary }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.5 }}
              >
                Подарок от {toGenitive(senderName)}
              </motion.p>
            )}

            {/* CTA Button */}
            <motion.button
              onClick={handleOpen}
              disabled={isOpening}
              className="relative z-10 px-10 py-4 rounded-full text-white text-[16px] font-semibold mt-4 overflow-hidden"
              style={{
                background: isDark
                  ? "linear-gradient(135deg, #D4AF37, #B8962E)"
                  : `linear-gradient(135deg, ${accentColor}, ${adjustColor(accentColor, -30)}, #E8A87C)`,
                boxShadow: isDark
                  ? "0 10px 40px rgba(212,175,55,0.25), 0 0 20px rgba(212,175,55,0.1)"
                  : `0 10px 40px ${accentColor}30, 0 0 20px ${accentColor}10`,
                animation: isOpening ? undefined : "gift-cta-pulse 4s ease-in-out infinite",
              }}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.4 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              {/* Ripple / shimmer layer */}
              <span
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.15) 50%, transparent 60%)",
                  backgroundSize: "200% 100%",
                  animation: "gift-btn-shimmer 3s ease-in-out infinite",
                }}
              />
              <span className="relative">✨ Открыть подарок</span>
            </motion.button>
          </motion.div>
        ) : (
          /* ==================== OPENED CONTENT ==================== */
          <motion.div
            key="content"
            className="w-full max-w-[420px] mx-auto flex flex-col items-center gap-0 relative z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
          >
            {/* Main Glass Card */}
            <motion.div
              className="w-full rounded-[24px] sm:rounded-[32px] px-4 sm:px-6 pt-6 sm:pt-8 pb-5 sm:pb-7 flex flex-col items-center gap-4 sm:gap-5 relative overflow-hidden backdrop-blur-xl"
              style={{
                background: theme.cardBg,
                boxShadow: theme.cardShadow,
                border: theme.cardBorder,
              }}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              {/* Shimmer overlay on first load */}
              {showShimmer && (
                <div
                  className="absolute inset-0 z-20 pointer-events-none"
                  style={{
                    background: isDark
                      ? "linear-gradient(105deg, transparent 40%, rgba(212,175,55,0.06) 50%, transparent 60%)"
                      : "linear-gradient(105deg, transparent 40%, rgba(108,99,255,0.05) 50%, transparent 60%)",
                    backgroundSize: "200% 100%",
                    animation: "gift-shimmer 2s ease-in-out forwards",
                  }}
                />
              )}

              {/* Top inner glow */}
              <div
                className="absolute -top-20 left-1/2 -translate-x-1/2 w-[400px] h-[250px] pointer-events-none z-0"
                style={{
                  background: isDark
                    ? "radial-gradient(ellipse at center, rgba(212,175,55,0.07), transparent 65%)"
                    : "radial-gradient(ellipse at center, rgba(108,99,255,0.06), transparent 65%)",
                }}
              />

              {/* Badge */}
              <motion.div
                className="relative z-10 px-4 py-1.5 rounded-full text-[10px] tracking-[0.15em] uppercase font-semibold"
                style={{ background: theme.badgeBg, color: theme.badgeText }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.25, duration: 0.4 }}
              >
                🎧 Персональный хит
              </motion.div>

              {/* Hero name — large */}
              <motion.div
                className="text-center relative z-10"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <h1
                  className="text-[28px] sm:text-[32px] font-bold tracking-[0.3px] leading-tight"
                  style={{ color: theme.textPrimary }}
                >
                  {gift.hero_name}
                </h1>
                <p className="text-[14px] mt-2" style={{ color: theme.textSecondary }}>
                  Этот хит создан специально для тебя ✨
                </p>
                {senderName && (
                  <p className="text-[13px] mt-1.5 font-medium" style={{ color: isDark ? accentColor : theme.textTertiary }}>
                    Подарок от {toGenitive(senderName)}
                  </p>
                )}
              </motion.div>

              {/* Player — integrated with cover */}
              <motion.div
                className="w-full relative z-10"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                {proxyStatus === "blocked" && (
                  <div
                    className="mb-3 mx-auto max-w-[340px] flex items-start gap-2 px-3 py-2 rounded-lg text-[12px] leading-snug"
                    style={{
                      background: isDark ? "rgba(255,180,80,0.08)" : "rgba(255,165,0,0.08)",
                      color: isDark ? "rgba(255,210,140,0.9)" : "rgba(140,80,0,0.9)",
                      border: `1px solid ${isDark ? "rgba(255,180,80,0.2)" : "rgba(255,165,0,0.2)"}`,
                    }}
                  >
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      У некоторых провайдеров аудио может не воспроизводиться. Если песня молчит — попробуйте мобильный интернет или VPN.
                    </span>
                  </div>
                )}
                <GiftPlayerCard
                  trackTitle={gift.track_title}
                  heroName="HitChef"
                  audioUrl={
                    gift.slug && proxyStatus === "ok"
                      ? `https://hitchef.ru/functions/v1/audio-proxy?slug=${encodeURIComponent(gift.slug)}`
                      : gift.audio_url
                  }
                  coverUrl={gift.cover_url}
                  accentColor={accentColor}
                  isDark={isDark}
                  coverGradient={coverGradient}
                  onFirstPlay={() => {
                    setHasPlayed(true);
                    if (gift?.id) incrementPlays.mutate(gift.id);
                  }}
                  onPlayCountChange={(count) => setPlayCount(count)}
                  onCompletionChange={(pct) => setCompletionPercent(pct)}
                />
              </motion.div>

              {/* Custom message */}
              {customMessage && (
                <motion.div
                  className="w-full relative z-10"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.4 }}
                >
                  <GiftMessageCard
                    message={customMessage}
                    senderName={senderName}
                    theme={theme}
                    isDark={isDark}
                    accentColor={accentColor}
                  />
                </motion.div>
              )}

              {/* Share Booster */}
              <motion.div
                className="w-full relative z-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.55, duration: 0.4 }}
              >
                <GiftShareBooster
                  pageUrl={pageUrl}
                  heroName={gift.hero_name}
                  theme={theme}
                  giftId={gift.id}
                  accentColor={accentColor}
                  isDark={isDark}
                  slug={gift.slug}
                  playCount={playCount}
                  completionPercent={completionPercent}
                />
              </motion.div>
            </motion.div>

            {/* Footer */}
            <motion.a
              href="/"
              className="text-[10px] mt-4 transition-opacity hover:opacity-60"
              style={{ color: theme.textTertiary }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.75, duration: 0.3 }}
            >
              Создано в HitChef
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @keyframes gift-breathe {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        @keyframes gift-box-float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-8px) scale(1.02); }
        }
        @keyframes gift-glow-breathe {
          0%, 100% { opacity: 0.6; transform: translateX(-50%) scale(1); }
          50% { opacity: 1; transform: translateX(-50%) scale(1.15); }
        }
        @keyframes gift-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes gift-cta-pulse {
          0%, 100% { box-shadow: inherit; }
          50% { filter: brightness(1.08); }
        }
        @keyframes gift-btn-shimmer {
          0%, 100% { background-position: -200% 0; }
          50% { background-position: 200% 0; }
        }
        @keyframes gift-lid-open {
          0% { transform: rotateX(0deg) translateY(0); }
          60% { transform: rotateX(-30deg) translateY(-8px); }
          100% { transform: rotateX(-45deg) translateY(-12px); opacity: 0.5; }
        }
        @keyframes gift-bow-left {
          0% { transform: rotate(0deg) translateX(0); }
          100% { transform: rotate(-15deg) translateX(-6px); }
        }
        @keyframes gift-bow-right {
          0% { transform: rotate(0deg) translateX(0); }
          100% { transform: rotate(15deg) translateX(6px); }
        }
      `}</style>
    </div>
  );
};

export default GiftPage;
